import { PATH } from 'src/constants/Path';
import { useProductActive } from 'src/hooks';
import { storage } from 'src/utilities/Storage';
import { isBelowDayDuration } from 'src/utilities/date';
import { LatestInfoCountPill, NewPill } from '../components';
import { Section } from '../type';
import {
  clickNotificationButton,
  trackTapCustomerManagementButton,
  trackTapHomeMenu,
  trackTapInvoiceButton,
  trackTapInvoiceListButton,
  trackTapInvoiceTransactionButton,
  trackTapVendorManagementButton,
} from './SidebarEvents';

export const SideBarItems = (): Section[] => {
  const { isAccountPayableNew, isAccountReceivableNew } = useProductActive();
  const firstLoginTime = storage.get('firstTimeLogin', '');

  return [
    {
      children: [
        {
          title: 'common_home',
          path: PATH.home,
          analyticEvent: 'home',
          onClick: trackTapHomeMenu,
        },
        {
          title: 'pageTitle_transactionReport',
          path: PATH.TRANSACTION_REPORT.path,
          children: [
            {
              title: 'pageTitle_accountStatement',
              path: PATH.TRANSACTION_REPORT.accountStatement,
              analyticEvent: 'account_statement',
            },
            {
              title: 'pageTitle_settlementReport',
              path: PATH.TRANSACTION_REPORT.settlementReport,
              analyticEvent: 'settlement_report',
            },
            {
              title: 'refundReport_title',
              path: PATH.TRANSACTION_REPORT.refundReport,
              analyticEvent: 'refund_report',
            },
          ],
        },
        {
          title: 'common_children',
          path: PATH.MULTI_ACCOUNT_MANAGEMENT.path,
          children: [
            {
              title: 'common_list_of_children',
              path: PATH.MULTI_ACCOUNT_MANAGEMENT.childrenList,
              analyticEvent: 'children_list',
            },
            {
              title: 'children_childrenStatement_title',
              path: PATH.MULTI_ACCOUNT_MANAGEMENT.childrenStatement,
              analyticEvent: 'children_statement',
            },
            {
              title: 'mam_Bulk_Disbursement_Child',
              path: PATH.MULTI_ACCOUNT_MANAGEMENT.bulkDisbursementChild,
              analyticEvent: 'children_bulk_disbursement',
            },
            {
              title: 'children_childrenSettlement_title',
              path: PATH.MULTI_ACCOUNT_MANAGEMENT.childSettlement,
              analyticEvent: 'children_settlement_report',
            },
          ],
        },
        {
          title: 'common_notification',
          titleAdditionalComponent: <LatestInfoCountPill />,
          path: PATH.notifications,
          onClick: clickNotificationButton,
        },
        {
          title: 'common_bridgingReport',
          path: PATH.BRIDGING_REPORT.path,
          children: [
            {
              title: 'pageTitle_cashOut',
              path: PATH.BRIDGING_REPORT.cashOut,
              analyticEvent: 'bridging_cash_out',
            },
            {
              title: 'pageTitle_cashIn',
              path: PATH.BRIDGING_REPORT.cashIn,
              analyticEvent: 'bridging_cash_in',
            },
            {
              title: 'pageTitle_detailTransaction',
              path: PATH.BRIDGING_REPORT.transactionDetail,
              analyticEvent: 'bridging_transaction_detail',
            },
          ],
        },
        {
          title: 'common_bridgingReport',
          path: PATH.BRIDGING_REPORT.pathV2,
          children: [
            {
              title: 'pageTitle_cashOut',
              path: PATH.BRIDGING_REPORT.cashOutV2,
              analyticEvent: 'bridging_cash_out_v2',
            },
            {
              title: 'pageTitle_cashIn',
              path: PATH.BRIDGING_REPORT.cashInV2,
              analyticEvent: 'bridging_cash_in_v2',
            },
            {
              title: 'pageTitle_detailTransaction',
              path: PATH.BRIDGING_REPORT.transactionDetailV2,
              analyticEvent: 'bridging_transaction_detail_v2',
            },
          ],
        },
        {
          title: 'common_txProduct_moreFeaturesDemo',
          path: PATH.moreFeatures,
          analyticEvent: 'more_features',
          titleAdditionalComponent: isBelowDayDuration(firstLoginTime, 30) && (
            <NewPill />
          ),
        },
      ],
    },
    {
      title: 'common_products',
      children: [
        {
          title: 'common_send_money',
          path: PATH.SEND_MONEY.bulkDisbursement,
          children: [
            {
              title: 'common_txProduct_international_transfer',
              path: PATH.SEND_MONEY.INTERNATIONAL_REMITTANNCE.list,
              analyticEvent: 'international_remittance',
            },
            {
              title: 'common_bulk_disbursement',
              path: PATH.SEND_MONEY.bulkDisbursement,
              analyticEvent: 'bulk_disbursement',
            },
            {
              title: 'common_txProduct_claim_fund',
              path: PATH.SEND_MONEY.CLAIM_FUND.path,
              analyticEvent: 'claim_fund',
            },
            {
              title: 'common_txProduct_api_disburse',
              path: PATH.SEND_MONEY.apiDisbursement,
              analyticEvent: 'api_disbursement',
            },
            {
              title: 'common_txProduct_api_biller',
              path: PATH.SEND_MONEY.apiBiller,
              analyticEvent: 'api_biller',
            },
          ],
        },
        {
          title: 'common_receiveMoney',
          path: PATH.RECEIVE_MONEY.PAYMENT_LINK.path,
          children: [
            {
              title: 'common_txProduct_payment_checkout',
              path: PATH.RECEIVE_MONEY.PAYMENT_LINK.path,
              children: [
                {
                  title: 'common_onetime',
                  path: PATH.RECEIVE_MONEY.PAYMENT_LINK.oneTime,
                  analyticEvent: 'payment_link_one_time',
                },
                {
                  title: 'common_reusable',
                  path: PATH.RECEIVE_MONEY.PAYMENT_LINK.reusable,
                  analyticEvent: 'payment_link_reusable',
                },
              ],
            },
            {
              title: 'common_txProduct_virtual_account',
              path: PATH.RECEIVE_MONEY.VIRTUAL_ACCOUNT.path,
              children: [
                {
                  title: 'pageTitle_createdVA',
                  path: PATH.RECEIVE_MONEY.VIRTUAL_ACCOUNT.createdVA,
                  analyticEvent: 'va_created',
                },
                {
                  title: 'pageTitle_incomingPayment',
                  path: PATH.RECEIVE_MONEY.VIRTUAL_ACCOUNT.incomingPayment,
                  analyticEvent: 'va_incoming_payment',
                },
              ],
            },
            {
              title: 'qris_static',
              path: PATH.RECEIVE_MONEY.qrisStatic,
              analyticEvent: 'qris_static',
              icon: 'qris',
            },
            {
              title: 'common_paymentRouting',
              path: PATH.RECEIVE_MONEY.paymentRouting,
              analyticEvent: 'payment_routing',
            },
            {
              title: 'common_eWallet',
              path: PATH.RECEIVE_MONEY.eWallet,
              analyticEvent: 'ewallet',
            },
          ],
        },
        {
          title: 'common_cashManagement',
          path: PATH.RECEIVE_MONEY.cashDepositMachine,
          children: [
            {
              title: 'pageTitle_CDM',
              path: PATH.RECEIVE_MONEY.cashDepositMachine,
            },
            {
              title: 'offlineCashDeposit_pageTitle',
              path: PATH.RECEIVE_MONEY.offlineCashDeposit,
              analyticEvent: 'cash_deposit',
            },
            {
              title: 'offlineCashWithdrawal_pageTitle',
              path: PATH.SEND_MONEY.offlineCashWithdrawal,
              analyticEvent: 'offline_cash_withdrawal',
            },
            {
              title: 'pageTitle_apiOfflineCashIn',
              path: PATH.RECEIVE_MONEY.offlineCashIn,
              analyticEvent: 'api_offline_cash_in',
            },
            {
              title: 'pageTitle_apiOfflineCashOut',
              path: PATH.SEND_MONEY.apiOfflineCashOut,
              analyticEvent: 'api_offline_cash_out',
            },
            {
              title: 'common_cashDelivery',
              path: PATH.RECEIVE_MONEY.cashDelivery,
            },
            {
              title: 'common_txProduct_EDC',
              path: PATH.RECEIVE_MONEY.EDC,
            },
          ],
        },
        {
          title: 'common_expenseManagement',
          path: PATH.EXPENSE_MANAGEMENT.path,
          children: [
            {
              title: 'corporateCard_PageTitle',
              path: PATH.EXPENSE_MANAGEMENT.CORPORATE_CARD.dashboard,
              analyticEvent: 'virtual_card',
            },
            {
              title: 'common_bridgingReport',
              path: PATH.EXPENSE_MANAGEMENT.BRIDGING_REPORT.path,
              analyticEvent: 'virtual_card_bridging_report',
            },
            {
              title: 'common_txProduct_reimbursement',
              path: PATH.EXPENSE_MANAGEMENT.REIMBURSEMENT.path,
              analyticEvent: 'reimbursement',
            },
          ],
        },
        {
          title: 'common_invoice',
          path: PATH.INVOICE.path,
          children: [
            {
              title: 'common_account_receivable',
              titleAdditionalComponent: isAccountReceivableNew && <NewPill />,
              path: PATH.ACCOUNT_RECEIVABLE.createInvoice,
              analyticEvent: 'invoice',
              onClick: trackTapInvoiceButton,
            },
            {
              title: 'accountPayable_sidebar_accountPayable',
              path: PATH.ACCOUNT_PAYABLE.path,
              titleAdditionalComponent: isAccountPayableNew && <NewPill />,
              children: [
                {
                  title: 'common_invoiceList',
                  path: PATH.ACCOUNT_PAYABLE.invoiceList,
                  analyticEvent: 'account_payable',
                  onClick: trackTapInvoiceListButton,
                },
                {
                  title: 'accountPayable_sidebar_invoiceTransaction',
                  path: PATH.ACCOUNT_PAYABLE.invoiceTransaction,
                  analyticEvent: 'account_payable',
                  onClick: trackTapInvoiceTransactionButton,
                },
              ],
            },
            {
              title: 'common_customerManagement',
              path: PATH.ACCOUNT_RECEIVABLE.customerManagement,
              analyticEvent: 'customer_management',
              onClick: trackTapCustomerManagementButton,
            },
            {
              title: 'common_vendorManagement',
              path: PATH.ACCOUNT_PAYABLE.vendorManagement,
              analyticEvent: 'account_payable',
              onClick: trackTapVendorManagementButton,
            },
          ],
        },
        {
          title: 'common_supportingFeature',
          path: PATH.MORE_NON_TX.path,
          children: [
            {
              title: 'common_txProduct_api_inquiry',
              path: PATH.apiInquiry,
              analyticEvent: 'api_inquiry',
            },
            {
              title: 'pageTitle_apiTransactionsData',
              path: PATH.apiTransactionData,
              analyticEvent: 'api_transactions_data',
            },
          ],
        },
      ],
    },
    {
      title: 'common_other',
      children: [
        {
          title: 'sidebar_helpAndDocumentation',
          path: PATH.helpAndDocumentation,
          analyticEvent: 'help_and_documentation',
        },
      ],
    },
  ];
};
